// ─── generators/dashboard.js ──────────────────────────────────────────────────
// Rapportgenerator: genDashboard.
// Wijzig hier bij aanpassingen aan dit specifieke rapporttype.

import { MAANDEN, MAANDEN_K, fmtE, fmtD, fmtKg, fmtK, diff, statusLabel, statusBadge, isOnderweg } from '../helpers.js';
import { header, kpiBlok, pageCss, sectTitel, sectTitelKlein, footer, wrapMetHerhalendeHeader } from '../layout.js';
import { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek } from '../charts.js';
import { verwerkData } from '../data.js';

function genDashboard(aanvragen, bedrijfsnaam, cfg, logo) {
  // Filter aanvragen op geselecteerde periode
  const periodeType = cfg.dashPeriodeType || 'jaar';
  let gefilterd = aanvragen;
  let periodeLabel = String(cfg.jaar || new Date().getFullYear());
  if(periodeType === 'kwartaal') {
    const j = cfg.jaar || new Date().getFullYear();
    const q = cfg.kwartaal || 1;
    const mStart = (q-1)*3, mEind = q*3-1;
    gefilterd = aanvragen.filter(a => {
      const d = new Date(a.Datum||0);
      return d.getFullYear()===j && d.getMonth()>=mStart && d.getMonth()<=mEind;
    });
    periodeLabel = `Q${q} ${j}`;
  } else if(periodeType === 'maand') {
    const j = cfg.jaar || new Date().getFullYear();
    const m = cfg.maand ?? new Date().getMonth();
    const MAANDEN_V = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];
    gefilterd = aanvragen.filter(a => {
      const d = new Date(a.Datum||0);
      return d.getFullYear()===j && d.getMonth()===m;
    });
    periodeLabel = `${MAANDEN_V[m]} ${j}`;
  } else if(periodeType === 'custom' && (cfg.dashVan||cfg.dashTot)) {
    gefilterd = aanvragen.filter(a => {
      const dt = a.Datum||a.LaadDatum||'';
      if(cfg.dashVan && dt < cfg.dashVan) return false;
      if(cfg.dashTot && dt > cfg.dashTot) return false;
      return true;
    });
    periodeLabel = `${cfg.dashVan||'…'} t/m ${cfg.dashTot||'…'}`;
  }
  const huidigJaar = cfg.jaar || new Date().getFullYear();
  const vorigjaar  = huidigJaar - 1;
  const heeftVergelijk = cfg.inclVergelijk !== false;
  const d  = verwerkData(gefilterd, huidigJaar, heeftVergelijk ? vorigjaar : null);
  // Verwerkt vorig jaar apart voor dubbele grafiek
  const dV = heeftVergelijk ? verwerkData(aanvragen.filter(a=>{
    const dt = new Date(a.Datum||a.LaadDatum||0);
    return dt.getFullYear() === vorigjaar;
  }), vorigjaar, null) : null;
  const _css = '<style>' + pageCss(cfg.orientatie) + '</style>';
  const _hdr = header('Managementrapport', periodeLabel, bedrijfsnaam, logo);
  let body = '';

  const gtB = cfg.grafiekTypeBestemming||'hbalk';
  const gtR = cfg.grafiekTypeRoutes||'hbalk';
  const gtG = cfg.grafiekTypeGoed||'taart';
  const gt  = cfg.grafiekType||'balk';

  // Blok wrapper met randlijn
  const blok = (titel, inhoud, accent, mb='10px') => `
    <div class="blok" style="border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px;margin-bottom:${mb};page-break-inside:avoid;break-inside:avoid;${accent?`border-top:3px solid ${accent};`:''}">
      <div class="sectie-titel" style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.07em;margin-bottom:7px;">${titel}</div>
      ${inhoud}
    </div>`;

  const liggend = (cfg.orientatie||'staand') === 'liggend';

  // Grafiekhoogte: voor staand meer ruimte, liggend compacter (alles naast elkaar)
  const BH = liggend ? 120 : 160;

  // ── KPI kaarten ──
  if(cfg.inclKpi) {
    body += kpiBlok([
      {lbl:'Totale transportkosten', val:d.totK>0?fmtE(d.totK):'—', sub:`${d.lijst.length} zendingen${d.vorigK>0?' '+diff(d.totK,d.vorigK):''}`, kleur:'#3b82f6'},
      {lbl:'Gemiddeld per zending',  val:d.gemK>0?fmtE(d.gemK):'—', sub:'Per transport', kleur:'#d4a848'},
      {lbl:'Totaal gewicht',         val:d.totGew>0?fmtKg(d.totGew):'—', sub:d.totColli>0?`${d.totColli} colli`:'', kleur:'#8b5cf6'},
      {lbl:'Gem. doorlooptijd',      val:d.gemDoorloop?`${d.gemDoorloop} dgn`:'—', sub:'Laad → Los', kleur:'#10b981'},
    ], 4, liggend);
  }

  // Grafiek inhoud helper — met optionele vergelijking vorig jaar
  const grafiekInhoud = (type, data, labels, kleur, hoogte) => {
    if(type==='taart'||type==='donut') {
      // Taart/donut: geen vergelijking mogelijk, toon huidig jaar + legenda
      const nzD=[], nzL=[];
      data.forEach((v,i)=>{if(v>0){nzD.push(v);nzL.push(labels[i]);}});
      return taartGrafiek(nzD, nzL, null, type==='donut', fmtK, hoogte);
    }
    // Balk/lijn: vergelijking als vorig jaar aan staat
    if(heeftVergelijk && dV && dV.kostenM.some(v=>v>0)) {
      const legenda = `<div style="display:flex;justify-content:flex-end;gap:12px;font-size:8px;margin-bottom:4px;">
        <span><span style="display:inline-block;width:10px;height:10px;background:#1a364c;border-radius:2px;margin-right:3px;vertical-align:middle;"></span>${huidigJaar}</span>
        <span><span style="display:inline-block;width:10px;height:${type==='lijn'?'3':'10'}px;background:#c2e6ef;border-radius:2px;margin-right:3px;vertical-align:middle;${type==='lijn'?'border-top:2px dashed #c2e6ef;background:transparent;':''}"></span>${vorigjaar}</span>
      </div>`;
      if(type==='lijn') return legenda + renderGrafiek('lijn', data, labels, kleur, hoogte-20, dV.kostenM);
      return legenda + dubbeleGrafiek(type, data, dV.kostenM, labels, '#1a364c', '#c2e6ef', hoogte-20);
    }
    return renderGrafiek(type, data, labels, kleur, hoogte-16);
  };

  if(liggend) {
    // ── LIGGEND: exacte hoogteverdeling, alles op 1 A4 ──
    // Landscape A4 body hoogte ≈ 734px (794px - 2×30px marges)
    // Header≈75px, KPI≈65px, margin≈15px → chart zone ≈ 579px
    // Gebruik 520px zodat footer ook past

    const ZONE_H  = 440;
    const GAP     = 6;
    const PAD_V   = 16;  // 8px top + 8px bottom per blok
    const TTL_H   = 20;  // titelhoogte

    const actief = [
      cfg.inclBestemming && d.topLanden.length > 0,
      cfg.inclRoutes     && d.topRoutes.length > 0,
      cfg.inclGoed       && d.topGoed.length > 0,
    ].filter(Boolean);

    const rN      = actief.length || 1;
    const BLOK_H  = Math.floor((ZONE_H - (rN - 1) * GAP) / rN);
    const R_CH    = BLOK_H - PAD_V - TTL_H;
    const PAD_BOT = 28;  // extra bottom padding voor X-as labels
    const L_CH    = ZONE_H - 8 - PAD_BOT - TTL_H - 4 - (heeftVergelijk && dV ? 22 : 0);

    const pLL = heeftVergelijk&&dV ? dV.topLanden : null;
    const pRL = heeftVergelijk&&dV ? dV.topRoutes : null;
    const pGL = heeftVergelijk&&dV ? dV.topGoed   : null;

    // Bouw rechter blokken
    const rechterHTML = [
      cfg.inclBestemming && d.topLanden.length>0
        ? { titel:'Top bestemmingen',     inhoud: renderRanking(d.topLanden,n=>n+'x',gtB,R_CH,pLL,true), accent:'#d4a848' } : null,
      cfg.inclRoutes     && d.topRoutes.length>0
        ? { titel:'Meest gebruikte routes', inhoud: renderRanking(d.topRoutes,n=>n+'x',gtR,R_CH,pRL,true), accent:'#10b981' } : null,
      cfg.inclGoed       && d.topGoed.length>0
        ? { titel:'Goederensoorten',       inhoud: renderRanking(d.topGoed,n=>n+'x',gtG,R_CH,pGL,true),   accent:'#8b5cf6' } : null,
    ].filter(Boolean);

    // Kostengrafiek inhoud
    const gtLabel = {balk:'Balkgrafiek',lijn:'Lijngrafiek',taart:'Taartgrafiek',donut:'Donut',balk3d:'3D Balkgrafiek'}[gt]||gt;
    const legenda = ''; // legenda wordt al toegevoegd door grafiekInhoud()

    // Expliciete rijhoogtes in rechter grid
    const rowTemplate = rechterHTML.map(()=>`${BLOK_H}px`).join(' ');

    body += `<div style="display:grid;grid-template-columns:55% 43%;gap:10px;height:${ZONE_H}px;page-break-inside:avoid;break-inside:avoid;">

      <!-- Links: kostengrafiek -->
      <div style="border:1px solid #e2e8f0;border-top:3px solid #3b82f6;border-radius:8px;padding:8px 12px 28px 12px;height:${ZONE_H}px;box-sizing:border-box;">
        <div style="display:flex;justify-content:space-between;align-items:center;height:${TTL_H}px;margin-bottom:4px;">
          <span style="font-size:8px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;">Kosten per maand — ${gtLabel}</span>
          ${legenda}
        </div>
        ${cfg.inclGrafiek ? grafiekInhoud(gt, d.kostenM, MAANDEN_K, '#1a364c', L_CH) : ''}
      </div>

      <!-- Rechts: gestapeld met exacte rijhoogtes -->
      <div style="display:grid;grid-template-rows:${rowTemplate};gap:${GAP}px;height:${ZONE_H}px;box-sizing:border-box;overflow:hidden;">
        ${rechterHTML.map(b=>`
          <div style="border:1px solid #e2e8f0;border-top:3px solid ${b.accent};border-radius:8px;padding:8px 10px;height:${BLOK_H}px;box-sizing:border-box;overflow:hidden;">
            <div style="font-size:7.5px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.06em;height:${TTL_H}px;display:flex;align-items:center;">${b.titel}</div>
            ${b.inhoud}
          </div>`).join('')}
      </div>
    </div>`;
  } else {
    // ── STAAND: originele layout ──
    if(cfg.inclGrafiek) {
      const gtLabel = {balk:'Balkgrafiek',lijn:'Lijngrafiek',taart:'Taartgrafiek',donut:'Donut'}[gt]||gt;
      body += blok(`Kosten per maand — ${gtLabel}`, grafiekInhoud(gt,d.kostenM,MAANDEN_K,'#1a364c',BH), '#3b82f6');
    }
    if(cfg.inclBestemming||cfg.inclRoutes) {
      const cols = cfg.inclBestemming&&cfg.inclRoutes ? '1fr 1fr' : '1fr';
      body += `<div style="display:grid;grid-template-columns:${cols};gap:10px;margin-bottom:10px;">`;
      const prevLanden = heeftVergelijk&&dV ? dV.topLanden : null;
      const prevRoutes = heeftVergelijk&&dV ? dV.topRoutes : null;

      if(cfg.inclBestemming) {
        const titel = heeftVergelijk&&dV ? `Top bestemmingen <span style="font-size:7px;color:#94a3b8;font-weight:400;">■ ${huidigJaar} ■ <span style="color:#c2e6ef;">${vorigjaar}</span></span>` : 'Top bestemmingen';
        body += blok(titel, renderRanking(d.topLanden,n=>n+'x',gtB,BH,prevLanden), '#d4a848','0');
      }
      if(cfg.inclRoutes) {
        const titel = heeftVergelijk&&dV ? `Meest gebruikte routes <span style="font-size:7px;color:#94a3b8;font-weight:400;">■ ${huidigJaar} ■ <span style="color:#c2e6ef;">${vorigjaar}</span></span>` : 'Meest gebruikte routes';
        body += blok(titel, renderRanking(d.topRoutes,n=>n+'x',gtR,BH,prevRoutes), '#10b981','0');
      }
      body += `</div>`;
    }
    if(cfg.inclGoed&&d.topGoed.length>0) {
      body += blok('Goederensoorten', renderRanking(d.topGoed,n=>n+'x',gtG,BH,heeftVergelijk&&dV?dV.topGoed:null), '#8b5cf6');
    }
  }

  return wrapMetHerhalendeHeader(_css, _hdr, body);
}

export { genDashboard };
